import { SupplierQualification } from '../../lib/api';

export interface SupplierQualificationGroup {
  /** Stable key — supplier id where present, else the name it grouped on. */
  key: string;
  supplierName: string;
  qualifications: SupplierQualification[];
  /** Counts for the group summary line. */
  approved: number;
  outstanding: number;
}

/** Statuses that mean a reviewer has finished with the questionnaire. */
const SETTLED = new Set(['approved', 'rejected', 'expired']);

/**
 * Group questionnaire responses by supplier, preserving the order suppliers
 * first appear in the (already sorted and filtered) list.
 *
 * The flat table repeated the supplier's name on every row, so a supplier with
 * three questionnaires read as three unrelated suppliers and there was no way
 * to see how far any one of them had got overall.
 *
 * Grouped on supplier id where available. Falling back to the name alone would
 * merge two genuinely different companies that happen to share one — rare, but
 * it silently misattributes a questionnaire when it happens.
 */
export function groupQualificationsBySupplier(
  qualifications: SupplierQualification[]
): SupplierQualificationGroup[] {
  const groups = new Map<string, SupplierQualificationGroup>();

  for (const qual of qualifications) {
    const key = qual.supplier != null
      ? `id:${qual.supplier}`
      : `name:${qual.supplier_name || 'unknown'}`;

    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        supplierName: qual.supplier_name || 'Unknown Supplier',
        qualifications: [],
        approved: 0,
        outstanding: 0,
      };
      groups.set(key, group);
    }

    group.qualifications.push(qual);
    if (qual.status === 'approved') {
      group.approved += 1;
    }
    if (!SETTLED.has(qual.status)) {
      group.outstanding += 1;
    }
  }

  return Array.from(groups.values());
}

/**
 * One line describing where a supplier has got to overall — the thing the flat
 * table could not say.
 */
export function summariseGroup(group: SupplierQualificationGroup): string {
  const total = group.qualifications.length;
  const noun = `questionnaire${total !== 1 ? 's' : ''}`;
  if (group.outstanding === 0) {
    return total === group.approved
      ? `${total} ${noun} · all approved`
      : `${total} ${noun} · none outstanding`;
  }
  if (group.approved > 0) {
    return `${total} ${noun} · ${group.approved} approved, ${group.outstanding} outstanding`;
  }
  return `${total} ${noun} · ${group.outstanding} outstanding`;
}
